import { Link } from 'react-router';

import type { GuestEvent } from '@mds/shared/types/guest-event';
import { cn } from '@mds/shared/lib/utils';
import { Button } from '@mds/shared/ui/shadcn/button';

import RuledList from '@/components/section/ruled-list';

import { formatEventDate, formatSpaceName, formatTicket } from '@/lib/format';

/**
 * The event page's registration panel: what it costs, when and where, and the
 * way in — on the dark panel, so it reads as the one thing on the page to act
 * on.
 *
 * Built from parts that already exist rather than drawn fresh: the facts are a
 * two-column `RuledList` and the action is the default `Button`. What this adds
 * is only the panel they sit on.
 */
export default function RegistrationCard({
  event,
  className,
}: {
  event: GuestEvent;
  className?: string;
}) {
  const { schedule, registrationStatus } = event;

  return (
    // `data-ground="panel"` repoints the colour roles for everything inside
    // the card: the Button's green, and the list's labels and rules. See the
    // `[data-ground='panel']` blocks in semantic.css and components.css.
    <div
      data-ground="panel"
      className={cn('flex flex-col rounded-card bg-panel p-card text-on-panel', className)}
    >
      {/* The heading block carries the space under it, so the list below
          takes no class of its own. */}
      <div className="pb-6">
        <span className="font-mono text-2xs text-brand uppercase">Registration</span>
        <p className="mt-2 text-5xl font-bold tracking-tight">
          {formatTicket(event.price?.amountMinor ?? 0)}
        </p>
        {event.priceNote && <p className="mt-3 text-base text-on-panel/70">{event.priceNote}</p>}
      </div>

      <RuledList
        accent="brand"
        align="end"
        items={[
          { marker: 'Date', description: formatEventDate(schedule.date) },
          { marker: 'Time', description: `${schedule.startTime} – ${schedule.endTime}` },
          { marker: 'Space', description: formatSpaceName(event.space) },
          // No seat count: the "Fully booked" button and the page's few-seats
          // warning already say what a visitor needs to know about room.
        ]}
      />

      {/* `mt-auto` sends the button to the bottom when the card is stretched
          taller than its content; `pt-6` is the gap that survives when it is
          not. The wrapper carries both so the Button's own classes stay its. */}
      <div className="mt-auto pt-6">
        {registrationStatus === 'closed' ? (
          // The server refuses a registration once the event has started, so
          // the way in is closed rather than offered and then refused on the
          // next page.
          <Button size="lg" className="w-full" disabled>
            Registration closed
          </Button>
        ) : (
          // A full event still takes registrations: they join the waitlist.
          <Button asChild size="lg" className="w-full">
            <Link to={`/events/${event.slug}/register`}>
              {registrationStatus === 'full'
                ? 'Register to join the waitlist'
                : event.isFree
                  ? 'Register free'
                  : 'Register'}
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}
